// src/pages/Dashboard/Admin/DepensesRecettesDashboard/components/AccountingKpiCards.jsx

import React from "react";
import {
  FaArchive,
  FaChartLine,
  FaExclamationTriangle,
  FaFileInvoice,
  FaLayerGroup,
  FaPercent,
  FaReceipt,
  FaTags,
} from "react-icons/fa";

import { fmtEUR } from "../utils/accountingDashboardUtils";

function KpiCard({ label, value, helper, icon, tone = "indigo", loading }) {
  const tones = {
    indigo:
      "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    emerald:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
    amber:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
    rose:
      "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300",
    slate:
      "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-300",
  };

  return (
    <div className={`rounded-[24px] border p-4 ${tones[tone]}`}>
      {loading ? (
        <div className="h-16 animate-pulse rounded-2xl bg-white/70 dark:bg-slate-900/50" />
      ) : (
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/80 text-lg dark:bg-slate-900/50">
            {icon}
          </div>

          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] opacity-75">
              {label}
            </p>

            <p className="mt-0.5 truncate text-xl font-bold text-slate-950 dark:text-white">
              {value}
            </p>

            {helper && (
              <p className="mt-0.5 truncate text-xs text-slate-600 dark:text-slate-300">
                {helper}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AccountingKpiCards({ stats, totals, loading }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <KpiCard
        loading={loading}
        label="Opérations"
        value={stats.count}
        helper={`${stats.depenseCount} dépenses · ${stats.recetteCount} recettes`}
        icon={<FaReceipt />}
        tone="indigo"
      />

      <KpiCard
        loading={loading}
        label="Solde"
        value={fmtEUR(totals.solde)}
        helper={totals.solde >= 0 ? "Solde positif" : "Solde négatif"}
        icon={<FaChartLine />}
        tone={totals.solde >= 0 ? "emerald" : "rose"}
      />

      <KpiCard
        loading={loading}
        label="Catégories"
        value={stats.categoriesCount}
        helper="Catégories utilisées"
        icon={<FaTags />}
        tone="slate"
      />

      <KpiCard
        loading={loading}
        label="Comptes"
        value={stats.plansCount}
        helper={`${stats.totalPlansAvailable} comptes disponibles`}
        icon={<FaLayerGroup />}
        tone="indigo"
      />

      <KpiCard
        loading={loading}
        label="Justificatifs"
        value={`${stats.coverageRate}%`}
        helper={`${stats.withDocument} opération(s) avec pièce/lien`}
        icon={<FaFileInvoice />}
        tone={stats.coverageRate >= 80 ? "emerald" : "amber"}
      />

      <KpiCard
        loading={loading}
        label="Sans compte"
        value={`${stats.missingPlanRate}%`}
        helper={`${stats.withoutPlan} opération(s) à affecter`}
        icon={<FaExclamationTriangle />}
        tone={stats.withoutPlan > 0 ? "amber" : "emerald"}
      />

      <KpiCard
        loading={loading}
        label="Top dépense"
        value={stats.topDepense ? fmtEUR(stats.topDepense.montant) : "—"}
        helper={stats.topDepense?.designation || "Aucune dépense"}
        icon={<FaArchive />}
        tone="rose"
      />

      <KpiCard
        loading={loading}
        label="Marge solde"
        value={
          stats.recette ? `${Math.round((stats.solde / stats.recette) * 100)}%` : "—"
        }
        helper="Solde / recettes"
        icon={<FaPercent />}
        tone={totals.solde >= 0 ? "emerald" : "rose"}
      />
    </div>
  );
}